import React, { useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import { Gender } from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import api from "@/utils/api";
import { displayError } from "@/utils/errors";
import { gtagEvent } from "@/utils/gtag";
import nutricoachLogo from "@assets/Nutricoach.png";
import Spacer from "@components/layout/Spacer";

import Step1Form from "./components/Step1Form";
import Step2Form from "./components/Step2Form";
import Step3Form from "./components/Step3Form";
import Step4Form from "./components/Step4Form";
import TransparentProgress from "./components/TransparentProgress";

const useStyles = makeStyles()({
  errorDialogContent: {
    whiteSpace: "pre-wrap",
  },
});

const validationSchema = yup
  .object({
    firstName: yup.string().label("First Name").required(),
    middleName: yup.string().label("Middle Name").nullable(),
    lastName: yup.string().label("Last Name").required(),
    email: yup.string().label("Email").required().email(),
    password: yup
      .string()
      .label("Password")
      .required()
      .min(12, "Password must contain a minimum of 12 characters."),
    gender: yup.string().label("Gender").presence().required().enum(Gender),
    dateOfBirth: yup
      .date()
      .label("Birthdate")
      .typeError("${label} is an invalid date")
      .nullable()
      .max(new Date()),
    mobileNumber: yup.string().label("Mobile number").required().phone(),
    country: yup.string().label("Country").required(),
    occupation: yup.string().label("Occupation").required(),
    expectedNumberOfClientsToHandle: yup.string(),
    whereDidYouHearAboutNutricoach: yup.string(),
    otherNutritionManagementSoftwareUsed: yup.string(),
    education: yup
      .array()
      .nullable()
      .of(
        yup
          .object()
          .required()
          .shape({
            name: yup.string().label("Institute name").required(),
            year: yup
              .number()
              .label("Year")
              .presence()
              .required()
              .max(new Date().getFullYear()),
          })
      ),
    trainings: yup
      .array()
      .nullable()
      .of(
        yup
          .object()
          .required()
          .shape({
            name: yup.string().label("Training name").required(),
            year: yup
              .number()
              .label("Year")
              .presence()
              .required()
              .max(new Date().getFullYear()),
          })
      ),
    certifications: yup
      .array()
      .nullable()
      .of(
        yup
          .object()
          .required()
          .shape({
            name: yup.string().label("Certificate name").required(),
            year: yup
              .number()
              .label("Year")
              .presence()
              .required()
              .max(new Date().getFullYear()),
          })
      ),
  })
  .defined();

export type FormValues = ReturnType<typeof validationSchema.cast>;

const RegisterPage: React.FC = () => {
  const { classes } = useStyles();
  const [step, setStep] = useState(1);
  const handleNextStep = () => setStep((prev) => prev + 1);
  const handlePrevStep = () => setStep((prev) => prev - 1);

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const showError = (message: string) => setErrorMessage(message);
  const hideError = () => setErrorMessage(null);

  const methods = useFormWithYup<FormValues>({
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      password: "",
      gender: "" as unknown as Gender,
      mobileNumber: "",
      dateOfBirth: null,
      country: "",
      occupation: "",
      expectedNumberOfClientsToHandle: "",
      whereDidYouHearAboutNutricoach: "",
      otherNutritionManagementSoftwareUsed: "",
      education: [],
      trainings: [],
      certifications: [],
    },
    mode: "onBlur",
    validationSchema,
  });

  const handleSubmit = async (formValues: unknown) => {
    const values = validationSchema.cast(formValues);

    try {
      const response = await api.post("coach_accounts", {
        coach_account: {
          email: values.email,
          password: values.password,
          coach_attributes: {
            profile_attributes: {
              first_name: values.firstName,
              middle_name: values.middleName,
              last_name: values.lastName,
              gender: values.gender.toLowerCase(),
              date_of_birth: values.dateOfBirth,
              mobile_number: values.mobileNumber,
              country: values.country,
              occupation: values.occupation,
            },
            coach_profile_attributes: {
              educations_attributes: values.education?.map((e) => ({
                school_year_end: e.year,
                name: e.name,
              })),
              trainings_attributes: values.trainings?.map((t) => ({
                year: t.year,
                name: t.name,
              })),
              certifications_attributes: values.certifications?.map((c) => ({
                year: c.year,
                name: c.name,
              })),
              expected_number_of_clients_to_handle:
                values.expectedNumberOfClientsToHandle,
              where_did_you_hear_about_nutricoach:
                values.whereDidYouHearAboutNutricoach,
              other_nutrition_management_software_used:
                values.otherNutritionManagementSoftwareUsed,
            },
          },
        },
      });
      if (response.ok) {
        gtagEvent("sign_up");
        navigate("/register-success", { replace: true });
      } else {
        const errorMessage = displayError(await response.json());
        showError(errorMessage);
      }
    } catch (e) {
      const errorMessage = displayError(e);
      showError(errorMessage);
    }
  };

  const progressValue = step * 25 - 25;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      margin="auto"
      maxWidth={1020}
      height="100vh"
    >
      <Spacer pxVertical={28} />
      <img src={nutricoachLogo} alt="nutricoach full logo" />
      <Spacer pxVertical={28} />
      <TransparentProgress variant="determinate" value={progressValue} />
      <form
        style={{ width: "100%" }}
        onSubmit={methods.handleSubmit(handleSubmit)}
      >
        <FormProvider {...methods}>
          {step === 1 && <Step1Form onNextStep={handleNextStep} />}
          {step === 2 && (
            <Step2Form
              onNextStep={handleNextStep}
              onPreviousStep={handlePrevStep}
            />
          )}
          {step === 3 && (
            <Step3Form
              onNextStep={handleNextStep}
              onPreviousStep={handlePrevStep}
            />
          )}
          {step === 4 && <Step4Form onPreviousStep={handlePrevStep} />}
        </FormProvider>
      </form>
      <Dialog
        open={Boolean(errorMessage)}
        onClose={hideError}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">
          Unable to register your account
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="error-dialog-description"
            className={classes.errorDialogContent}
          >
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={hideError} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisterPage;
